import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/admin/_layout/inventory/bills")({
  component: BillsPage,
});

import { useState, useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { MoreHorizontal, Settings2 } from "lucide-react";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import SearchFilter from "@/components/reusable-components/header/search-filter";
import { SelectFilter } from "@/components/crud/filters/SelectFilter";
import { RemoteSearchSelect } from "@/components/reusable-components/remote-search-select";
import { VendorSearchSelect } from "@/components/inventory/shared/VendorSearchSelect";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import {
  useFetchBills,
  useBillMutations,
  type BillOrdering,
} from "@/hooks/api/inventory/useBills";
import { useDataTableOrdering } from "@/hooks/ui/useDataTableOrdering";
import type { SortDirection } from "@/components/reusable-components/sort-arrow";
import { useDebounce } from "@/hooks/use-debounce";
import {
  fetchPurchaseOrders,
  usePurchaseOrderMutations,
} from "@/hooks/api/inventory/useStockOps";
import { inventoryKeys } from "@/hooks/api/inventory/keys";
import {
  useFetchInventorySettings,
  useUpdateInventorySettings,
} from "@/hooks/api/inventory/useInventorySettings";
import {
  useFetchItems,
  useFetchWarehouses,
  useFetchUnits,
  ALL_ACTIVE_PARAMS,
} from "@/hooks/api/inventory/useCatalog";
import { BillTable } from "@/components/inventory/tables/BillTable";
import { useMyPermissions } from "@/hooks/api/authorization/useAuthorization";
import { can } from "@/lib/permissions";
import { BillFormSheet } from "@/components/inventory/forms/BillFormSheet";
import { BillDetailSheet } from "@/components/inventory/forms/DetailViewSheet";
import { ReceiveGoodsModal } from "@/components/inventory/forms/ReceiveGoodsModal";
import { ReceiveBillModal } from "@/components/inventory/forms/ReceiveBillModal";
import instance from "@/lib/axios";
import { getInventoryUrl } from "@/lib/inventory";
import type {
  Bill,
  BillMarkReceivedPayload,
  BillPayload,
  BillStatus,
  PurchaseOrder,
  ReceiveGoodsPayload,
} from "@/types/inventory";

const STATUS_OPTIONS = [
  { label: "Draft", value: "DRAFT" },
  { label: "Received", value: "RECEIVED" },
  { label: "Paid", value: "PAID" },
  { label: "Overdue", value: "OVERDUE" },
  { label: "Void", value: "VOID" },
] satisfies { label: string; value: BillStatus }[];

function BillsPage() {
  const navigate = useNavigate();
  const { data: capabilities } = useMyPermissions();
  const canVoidBills = can(capabilities, "inventory.finance.manage");
  const { createBill, updateBill, markBillReceived, markBillPaid, voidBill } =
    useBillMutations();
  const isMutating =
    createBill.isPending ||
    updateBill.isPending ||
    markBillReceived.isPending ||
    markBillPaid.isPending ||
    voidBill.isPending;
  const { receiveGoods } = usePurchaseOrderMutations();
  const poMutLoading = receiveGoods.isPending;
  // Only needed for BillFormSheet's manual "no PO" item picker/warehouse
  // select (Direct Bill Creation) -- see docs/inventory-settings-plan.md.
  const catalogItemsQuery = useFetchItems(ALL_ACTIVE_PARAMS);
  const catalogItems = catalogItemsQuery.data?.results ?? [];
  const warehousesQuery = useFetchWarehouses(ALL_ACTIVE_PARAMS);
  const warehouses = warehousesQuery.data?.results ?? [];
  const unitsQuery = useFetchUnits(ALL_ACTIVE_PARAMS);
  const units = unitsQuery.data?.results ?? [];

  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();
  const [statusFilter, setStatusFilter] = useState<BillStatus | undefined>(
    undefined
  );
  const [vendorFilter, setVendorFilter] = useState<string | undefined>(
    undefined
  );
  const [poFilter, setPoFilter] = useState<string | undefined>(undefined);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, resetDebounce] = useDebounce(searchInput, 500);
  const [showFilters, setShowFilters] = useState(false);
  const [viewTarget, setViewTarget] = useState<Bill | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editing, setEditing] = useState<Bill | null>(null);
  const [receivedTarget, setReceivedTarget] = useState<Bill | null>(null);
  const [serializedReceiveTarget, setSerializedReceiveTarget] =
    useState<Bill | null>(null);
  const [paidTarget, setPaidTarget] = useState<Bill | null>(null);
  const [voidTarget, setVoidTarget] = useState<Bill | null>(null);

  const { ordering, toggleOrdering, getApiOrdering, setOrdering } =
    useDataTableOrdering<
      {
        bill_number: SortDirection;
        status: SortDirection;
        bill_date: SortDirection;
        due_date: SortDirection;
        created_at: SortDirection;
        updated_at: SortDirection;
      },
      BillOrdering
    >({
      bill_number: null,
      status: null,
      bill_date: null,
      due_date: null,
      created_at: null,
      updated_at: null,
    });

  // Two-way synced with the Inventory Settings page -- both read/write the
  // same InventorySettings.auto_receive_po_on_bill_received, so a change
  // made here or there is the same one value everywhere. See
  // docs/inventory-settings-plan.md Decision 6.
  const inventorySettingsQuery = useFetchInventorySettings();
  const autoReceivePO =
    inventorySettingsQuery.data?.auto_receive_po_on_bill_received ?? false;
  const updateInventorySettings = useUpdateInventorySettings();
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [poToReceive, setPoToReceive] = useState<PurchaseOrder | null>(null);

  const billsQuery = useFetchBills({
    page,
    page_size: pageSize,
    search: debouncedSearch || undefined,
    ordering: getApiOrdering(),
    status: statusFilter,
    vendor: vendorFilter ? Number(vendorFilter) : undefined,
    purchase_order: poFilter ? Number(poFilter) : undefined,
  });
  const bills = billsQuery.data?.results ?? [];
  const count = billsQuery.data?.count ?? 0;

  const isLoading = billsQuery.isPending || isMutating;
  const isFetching = billsQuery.isFetching || isMutating;

  useEffect(() => {
    setPage(1);
  }, [
    debouncedSearch,
    ordering,
    statusFilter,
    vendorFilter,
    poFilter,
    setPage,
  ]);

  const handleToggleAutoReceive = async (checked: boolean) => {
    try {
      await updateInventorySettings.mutateAsync({
        auto_receive_po_on_bill_received: checked,
      });
    } catch {
      toast.error("Couldn't update the auto-receive setting");
    }
  };

  const resetFilters = () => {
    setOrdering({
      bill_number: null,
      status: null,
      bill_date: null,
      due_date: null,
      created_at: null,
      updated_at: null,
    });
    setSearchInput("");
    resetDebounce();
    setStatusFilter(undefined);
    setVendorFilter(undefined);
    setPoFilter(undefined);
    setPage(1);
  };

  const handlePageChange = (p: number) => {
    setPage(p);
  };

  const handleOpenSheet = () => {
    setIsSheetOpen(true);
  };

  const handleOpenEditSheet = (bill: Bill) => {
    setEditing(bill);
    setIsSheetOpen(true);
  };

  const handleSubmit = async (payload: BillPayload) => {
    if (editing) {
      await updateBill.mutateAsync({ id: editing.id, payload });
      toast.success("Bill updated");
    } else {
      await createBill.mutateAsync(payload);
      toast.success("Bill created");
    }
    setIsSheetOpen(false);
    setEditing(null);
  };

  // A direct (PO-less) bill with at least one SERIALIZED line needs its own
  // identifier-collection step before it can post stock -- everything else
  // (bulk-only direct bills, and every PO-linked bill regardless of its own
  // lines' tracking modes) keeps the existing single-click confirm flow.
  const handleMarkReceivedClick = (bill: Bill) => {
    const needsSerializedReceive =
      !bill.purchase_order &&
      !!bill.warehouse &&
      bill.items.some((i) => i.item_tracking_mode === "SERIALIZED");
    if (needsSerializedReceive) {
      setSerializedReceiveTarget(bill);
    } else {
      setReceivedTarget(bill);
    }
  };

  const handleSerializedReceiveSubmit = async (
    payload: BillMarkReceivedPayload
  ) => {
    if (!serializedReceiveTarget) return;
    const bill = serializedReceiveTarget;
    await markBillReceived.mutateAsync({ id: bill.id, payload });
    toast.success(`${bill.bill_number} marked received`);
    setSerializedReceiveTarget(null);
  };

  const handleReceivedConfirm = async () => {
    if (!receivedTarget) return;
    const bill = receivedTarget;
    setReceivedTarget(null);
    try {
      await markBillReceived.mutateAsync({ id: bill.id });
      toast.success(`${bill.bill_number} marked received`);

      if (autoReceivePO && bill.purchase_order) {
        // The server already auto-received the PO's bulk lines as part of
        // mark-received when the setting is on (see mark_bill_received) --
        // only prompt here if the PO still has something outstanding
        // (its serialized lines, which can never be auto-received). A
        // fully-bulk PO is already RECEIVED by now and this never fires.
        try {
          const poRes = await instance.get<PurchaseOrder>(
            getInventoryUrl(`purchase-orders/${bill.purchase_order}/`)
          );
          const po = poRes.data;
          if (po.status === "ORDERED" || po.status === "PARTIALLY_RECEIVED") {
            setPoToReceive(po);
          }
        } catch {
          toast.error("Failed to load the linked purchase order");
        }
      }
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handlePoReceiveSubmit = async (payload: ReceiveGoodsPayload) => {
    if (!poToReceive) return;
    await receiveGoods.mutateAsync({ id: poToReceive.id, payload });
    toast.success(`PO ${poToReceive.po_number} received`);
    setPoToReceive(null);
  };

  const handlePaidConfirm = async () => {
    if (!paidTarget) return;
    try {
      await markBillPaid.mutateAsync({ id: paidTarget.id });
      toast.success(`${paidTarget.bill_number} marked paid`);
      setPaidTarget(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handleVoidConfirm = async () => {
    if (!voidTarget) return;
    try {
      await voidBill.mutateAsync({ id: voidTarget.id });
      toast.success(`${voidTarget.bill_number} voided`);
      setVoidTarget(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const receivedDialogDescription =
    autoReceivePO && receivedTarget?.purchase_order
      ? `Mark bill ${receivedTarget?.bill_number} as received? Its linked Purchase Order's outstanding bulk items will be received automatically; any serialized items will still need a manual receive.`
      : `Mark bill ${receivedTarget?.bill_number} as received?`;

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Bills"
          description="Manage vendor bills"
          onToggleFilter={() => setShowFilters((prev) => !prev)}
          onAdd={handleOpenSheet}
          addLabel="New Bill"
          isLoading={isFetching}
        >
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={isFetching}>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                className="cursor-pointer"
                onClick={() => setShowSettingsDialog(true)}
              >
                <Settings2 className="mr-2 h-4 w-4" />
                Auto-receive PO
                <span
                  className={`ml-auto text-xs font-medium ${autoReceivePO ? "text-green-600" : "text-muted-foreground"}`}
                >
                  {autoReceivePO ? "On" : "Off"}
                </span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </PageHeader>

        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          <SearchFilter
            placeholder="Search bill no..."
            value={searchInput}
            onChange={setSearchInput}
          />
          <SelectFilter
            placeholder="Status"
            options={STATUS_OPTIONS}
            value={statusFilter}
            onChange={(v) => setStatusFilter(v)}
          />
          <VendorSearchSelect
            className="w-fit"
            value={vendorFilter ?? null}
            onChange={(v) => setVendorFilter(v == null ? undefined : String(v))}
            placeholder="Vendor"
          />
          <RemoteSearchSelect<PurchaseOrder>
            className="w-fit"
            queryKey={inventoryKeys.purchaseOrders.list()}
            fetchPage={fetchPurchaseOrders}
            getId={(po) => po.id}
            getLabel={(po) => po.po_number}
            value={poFilter ? Number(poFilter) : null}
            onChange={(v) => setPoFilter(v == null ? undefined : String(v))}
            placeholder="Purchase Order"
            searchPlaceholder="Search purchase orders..."
            noItemsMessage="No purchase orders found."
          />
        </FiltersPanel>

        <BillTable
          bills={bills}
          isLoading={isLoading}
          ordering={ordering}
          onOrderingChange={toggleOrdering}
          onNameClick={(bill) =>
            navigate({
              to: "/admin/inventory/bills/$id",
              params: { id: String(bill.id) },
            })
          }
          onView={(bill) => setViewTarget(bill)}
          onEdit={handleOpenEditSheet}
          onMarkReceived={handleMarkReceivedClick}
          onMarkPaid={(bill) => setPaidTarget(bill)}
          onVoid={(bill) => setVoidTarget(bill)}
          canVoidBills={canVoidBills}
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={handlePageChange}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />

        <BillFormSheet
          isOpen={isSheetOpen}
          onClose={() => {
            setIsSheetOpen(false);
            setEditing(null);
          }}
          onSubmit={handleSubmit}
          warehouses={warehouses}
          items={catalogItems}
          units={units}
          isLoading={isMutating}
          editing={editing}
        />

        {/* Auto-receive PO settings dialog */}
        <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
          <DialogContent className="sm:max-w-[420px]">
            <DialogHeader>
              <DialogTitle>Auto-receive Purchase Order</DialogTitle>
              <DialogDescription>
                Also editable from Inventory → Settings → Configuration this is
                the same org-wide setting, either place updates it.
              </DialogDescription>
            </DialogHeader>

            <div className="flex items-center justify-between gap-4 rounded-lg border p-4">
              <div className="space-y-1">
                <Label className="text-sm font-medium">
                  Auto-receive PO on bill received
                </Label>
                <p className="text-xs text-justify text-muted-foreground">
                  When enabled, marking a bill as Received automatically
                  receives the linked Purchase Order's outstanding bulk
                  (non-serialized) items too. Serialized items always still need
                  a manual receive.
                </p>
              </div>
              <Switch
                checked={autoReceivePO}
                onCheckedChange={handleToggleAutoReceive}
                disabled={updateInventorySettings.isPending}
                className="mt-0.5 shrink-0"
              />
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowSettingsDialog(false)}
              >
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <DeleteConfirmDialog
          isOpen={!!receivedTarget}
          onClose={() => setReceivedTarget(null)}
          onConfirm={handleReceivedConfirm}
          title="Mark Bill Received"
          description={receivedDialogDescription}
          itemName={receivedTarget?.bill_number}
          isLoading={isMutating}
          destructive={false}
          confirmLabel="Mark Received"
          loadingLabel="Marking..."
        />

        <DeleteConfirmDialog
          isOpen={!!paidTarget}
          onClose={() => setPaidTarget(null)}
          onConfirm={handlePaidConfirm}
          title="Mark Bill Paid"
          description={`Mark bill ${paidTarget?.bill_number} as paid?`}
          itemName={paidTarget?.bill_number}
          isLoading={isMutating}
          destructive={false}
          confirmLabel="Mark Paid"
          loadingLabel="Marking..."
        />

        <DeleteConfirmDialog
          isOpen={!!voidTarget}
          onClose={() => setVoidTarget(null)}
          onConfirm={handleVoidConfirm}
          title="Void Bill"
          description={`Void bill ${voidTarget?.bill_number}? This cannot be undone.`}
          itemName={voidTarget?.bill_number}
          isLoading={isMutating}
          destructive
          confirmLabel="Void Bill"
          loadingLabel="Voiding..."
        />
      </div>

      <BillDetailSheet
        isOpen={!!viewTarget}
        onClose={() => setViewTarget(null)}
        bill={viewTarget}
      />

      <ReceiveGoodsModal
        isOpen={!!poToReceive}
        onClose={() => setPoToReceive(null)}
        onSubmit={handlePoReceiveSubmit}
        purchaseOrder={poToReceive}
        isLoading={poMutLoading}
      />

      <ReceiveBillModal
        isOpen={!!serializedReceiveTarget}
        onClose={() => setSerializedReceiveTarget(null)}
        onSubmit={handleSerializedReceiveSubmit}
        bill={serializedReceiveTarget}
        isLoading={markBillReceived.isPending}
      />
    </SidebarInset>
  );
}
